import React from 'react';
import {
  CaraWorkspaceArtifactType,
  type CaraWorkspaceFieldsFragment,
  type HtmlDocumentTemplateFieldsFragment,
  type ProducerInformationFieldsFragment
} from '~/api/graphql';

import { HtmlDocumentFocus } from './artifact-focus-html-document';
import { TemplateFormFocus } from './artifact-focus-template-form';
import { IframeArtifactFocus } from './artifact-focus-pdf-form';
import { ApplicationFileFocus } from './artifact-focus-application-file';
import { EmailFocus } from './artifact-focus-email';
import { useEmailTemplateSession, useShowRedraftedVersion } from './hooks/use-template-redraft';
import type { SaveStatus } from '~/components/document-editor/artifact-focus-html-document-save-status-indicator';
import {
  applyWorkspaceTickToArtifact,
  type FocusedArtifact
} from '~/components/workspaces/lib/email-artifact-body';

const APPLICATION_FILE_VARIANTS: Partial<Record<CaraWorkspaceArtifactType, 'xlsx' | 'docx'>> = {
  [CaraWorkspaceArtifactType.XlsxFile]: 'xlsx',
  [CaraWorkspaceArtifactType.DocxFile]: 'docx'
};

/** Keeps a template redraft's progress across the versions Cara writes while it runs. */
const EmailFocusAcrossVersions = ({
  onVersionCreated,
  ...props
}: Omit<React.ComponentProps<typeof EmailFocus>, 'templateSession'> & {
  onVersionCreated?: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
}) => {
  const templateSession = useEmailTemplateSession(props.workspace.id, props.workspace.state);
  useShowRedraftedVersion({
    session: templateSession,
    workspace: props.workspace,
    artifact: props.artifact,
    showVersion: onVersionCreated
  });
  return (
    <EmailFocus
      // Version is in the key: each revision is a distinct composition.
      key={`${props.artifact.id}-${props.artifact.version}`}
      {...props}
      templateSession={templateSession}
    />
  );
};

export const ArtifactFocusDialog = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  canUpdate: boolean;
  artifact: FocusedArtifact;
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  agencyProducerInformations: ProducerInformationFieldsFragment[];
  userProducerInformations: ProducerInformationFieldsFragment[];
  onClose: () => void;
  onVersionChange: (artifact: FocusedArtifact) => void;
  onVersionCreated?: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
  onArtifactSaved?: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
  onConvertToTemplate?: () => void;
  isConvertingToTemplate?: boolean;
  autoOpenSaveDialog?: boolean;
  onSaveDialogClosed?: () => void;
  onSaveStatusChange?: (status: SaveStatus) => void;
  onUnsavedChangesChange?: (unsaved: boolean) => void;
  emailBodyHydrating?: boolean;
  /** For one email of a bulk draft; see EmailFocus. */
  bulkSendFromAddress?: string;
}) => {
  // Resolve the latest snapshot of this artifact from the live workspace; if it has been
  // removed (e.g. version revoked), close the dialog gracefully.
  const live = props.workspace.artifacts.find(
    (a) => a.id === props.artifact.id && a.version === props.artifact.version
  );
  if (!live) {
    props.onClose();
    return null;
  }
  // An EMAIL artifact's workspace copy carries no body, so the body the rail
  // fetched has to survive this re-resolve; see applyWorkspaceTickToArtifact.
  const artifact = applyWorkspaceTickToArtifact(props.artifact, live);

  // Every focus below seeds local state from the artifact at mount, so an
  // instance React reuses across a switch of artifacts goes on showing the one
  // before it -- in the composer, one email's recipients under another's words.
  // Email keys by version too: each revision is its own composition. Other
  // types leave version out so viewing an earlier version does not throw away
  // unsaved edits on the live one.
  switch (artifact.type) {
    case CaraWorkspaceArtifactType.DocumentHtml:
      return (
        <HtmlDocumentFocus
          key={artifact.id}
          workspace={props.workspace}
          artifact={artifact}
          canUpdate={props.canUpdate}
          htmlDocumentTemplates={props.htmlDocumentTemplates}
          onClose={props.onClose}
          onVersionChange={props.onVersionChange}
          onVersionCreated={props.onVersionCreated}
          onArtifactSaved={props.onArtifactSaved}
          onConvertToTemplate={props.onConvertToTemplate}
          isConvertingToTemplate={props.isConvertingToTemplate}
          autoOpenSaveDialog={props.autoOpenSaveDialog}
          onSaveDialogClosed={props.onSaveDialogClosed}
          onSaveStatusChange={props.onSaveStatusChange}
        />
      );
    case CaraWorkspaceArtifactType.PdfFile:
      return (
        <IframeArtifactFocus
          key={artifact.id}
          workspace={props.workspace}
          artifact={artifact}
          kind="pdf"
          onClose={props.onClose}
        />
      );
    case CaraWorkspaceArtifactType.CustomForm:
      return (
        <IframeArtifactFocus
          key={artifact.id}
          workspace={props.workspace}
          artifact={artifact}
          kind="custom-form"
          onClose={props.onClose}
        />
      );
    case CaraWorkspaceArtifactType.TemplateForm:
      return (
        <TemplateFormFocus
          key={artifact.id}
          workspace={props.workspace}
          artifact={artifact}
          agencyProducerInformations={props.agencyProducerInformations}
          userProducerInformations={props.userProducerInformations}
          onClose={props.onClose}
          onVersionChange={props.onVersionChange}
          onUnsavedChangesChange={props.onUnsavedChangesChange}
        />
      );
    case CaraWorkspaceArtifactType.Email:
      return (
        <EmailFocusAcrossVersions
          key={artifact.id}
          workspace={props.workspace}
          artifact={artifact}
          agencyProducerInformations={props.agencyProducerInformations}
          userProducerInformations={props.userProducerInformations}
          onClose={props.onClose}
          onVersionCreated={props.onVersionCreated}
          bodyHydrating={props.emailBodyHydrating}
          bulkSendFromAddress={props.bulkSendFromAddress}
          canUpdate={props.canUpdate}
        />
      );
    case CaraWorkspaceArtifactType.XlsxFile:
    case CaraWorkspaceArtifactType.DocxFile: {
      const variant = APPLICATION_FILE_VARIANTS[artifact.type];
      if (!variant) return null;
      return (
        <ApplicationFileFocus
          key={artifact.id}
          workspace={props.workspace}
          artifact={artifact}
          onClose={props.onClose}
          variant={variant}
        />
      );
    }
    default:
      return null;
  }
};
